import React, { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import { LanguageContext } from '../App';
import { translations } from '../i18n/translations';
import { apiConfig } from '../config/authConfig';
import { theme } from '../config/theme';

function Home() {
  const { instance, accounts } = useMsal();
  const { language, setLanguage } = useContext(LanguageContext);
  const t = translations[language];
  const navigate = useNavigate();
  const [containers, setContainers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const user = accounts[0];

  useEffect(() => { fetchContainers(); checkAdmin(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const getToken = async () => {
    const tokenResponse = await instance.acquireTokenSilent({
      scopes: [`api://${process.env.REACT_APP_CLIENT_ID}/access_as_user`],
      account: accounts[0],
    });
    return tokenResponse.accessToken;
  };

  const fetchContainers = async () => {
    try {
      const token = await getToken();
      const response = await fetch(`${apiConfig.baseUrl}/list-containers`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();
      setContainers(data.containers || []);
    } catch (err) {
      setError(t.error);
    } finally {
      setLoading(false);
    }
  };

  const checkAdmin = async () => {
    try {
      const token = await getToken();
      const response = await fetch(`${apiConfig.baseUrl}/is-admin`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();
      setIsAdmin(!!data.isAdmin);
    } catch (err) {
      setIsAdmin(false);
    }
  };

  const handleLogout = () => {
    instance.logoutRedirect().catch(console.error);
  };

  return (
    <div style={styles.container}>
      <header style={styles.header}>
        <div style={styles.headerLeft}>
          <div style={styles.logoGrid}>
            {[...Array(9)].map((_, i) => <span key={i} style={styles.logoCell} />)}
          </div>
          <span style={styles.headerTitle}>Archives</span>
        </div>
        <div style={styles.headerRight}>
          <div style={styles.langSelector}>
            <button onClick={() => setLanguage('fr')}
              style={language === 'fr' ? styles.langActive : styles.langBtn}>FR</button>
            <button onClick={() => setLanguage('en')}
              style={language === 'en' ? styles.langActive : styles.langBtn}>EN</button>
          </div>
          {isAdmin && (
            <button onClick={() => navigate('/admin')} style={styles.adminBtn}>
              {t.admin}
            </button>
          )}
          <span style={styles.userName}>{user?.name || user?.username}</span>
          <button onClick={handleLogout} style={styles.logoutBtn}>{t.logout}</button>
        </div>
      </header>

      <main style={styles.main}>
        <h1 style={styles.welcome}>
          {t.welcome}, {user?.name?.split(' ')[0]}
        </h1>
        <p style={styles.welcomeMsg}>{t.welcomeMessage}</p>

        <div style={styles.sectionTitle}>{t.yourContainers}</div>

        {loading && <p style={styles.info}>{t.loading}</p>}
        {error && <p style={styles.errorMsg}>{error}</p>}
        {!loading && !error && containers.length === 0 && (
          <p style={styles.info}>{t.noContainers}</p>
        )}

        <div style={styles.grid}>
          {containers.map((container) => (
            <div key={container} style={styles.card}
              onClick={() => navigate(`/explorer/${container}`)}
              onMouseEnter={e => { e.currentTarget.style.background = theme.colors.bgCardHover; e.currentTarget.style.borderColor = theme.colors.borderHover; }}
              onMouseLeave={e => { e.currentTarget.style.background = theme.colors.bgCard; e.currentTarget.style.borderColor = theme.colors.border; }}
            >
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none">
                <path d="M3 7a2 2 0 012-2h4l2 2h8a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2V7z" fill={theme.colors.accent}/>
              </svg>
              <span style={styles.cardName}>{container.toUpperCase()}</span>
            </div>
          ))}
        </div>
      </main>
    </div>
  );
}

const styles = {
  container: { minHeight: '100vh', backgroundColor: theme.colors.bg },
  header: {
    backgroundColor: theme.colors.bgHeader,
    padding: '0 24px',
    height: '56px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    borderBottom: `1px solid ${theme.colors.border}`,
  },
  headerLeft: { display: 'flex', alignItems: 'center', gap: '10px' },
  logoGrid: {
    width: '28px', height: '28px',
    backgroundColor: theme.colors.white,
    display: 'grid',
    gridTemplateColumns: '1fr 1fr 1fr',
    gridTemplateRows: '1fr 1fr 1fr',
    gap: '2px', padding: '4px', borderRadius: '2px',
  },
  logoCell: {
    backgroundColor: theme.colors.textDark,
    display: 'block', borderRadius: '1px',
  },
  headerTitle: {
    color: theme.colors.white,
    fontSize: theme.fonts.size.base,
    fontWeight: '600',
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
  },
  headerRight: { display: 'flex', alignItems: 'center', gap: '16px' },
  langSelector: { display: 'flex', gap: '4px' },
  langBtn: {
    border: `1px solid ${theme.colors.border}`,
    borderRadius: '3px', padding: '3px 8px',
    cursor: 'pointer', backgroundColor: 'transparent',
    color: theme.colors.textSecondary, fontSize: theme.fonts.size.xs,
  },
  langActive: {
    border: `1px solid ${theme.colors.white}`,
    borderRadius: '3px', padding: '3px 8px',
    cursor: 'pointer', backgroundColor: theme.colors.white,
    color: theme.colors.textDark, fontSize: theme.fonts.size.xs, fontWeight: '600',
  },
  adminBtn: {
    backgroundColor: 'transparent',
    border: `1px solid ${theme.colors.accent}`,
    borderRadius: '4px', color: theme.colors.accent,
    padding: '5px 12px', cursor: 'pointer', fontSize: theme.fonts.size.sm,
    fontWeight: '600',
  },
  userName: { color: theme.colors.textSecondary, fontSize: theme.fonts.size.base },
  logoutBtn: {
    backgroundColor: 'transparent',
    border: `1px solid ${theme.colors.border}`,
    borderRadius: '4px', color: theme.colors.textSecondary,
    padding: '5px 12px', cursor: 'pointer', fontSize: theme.fonts.size.sm,
  },
  main: { padding: '40px 32px' },
  welcome: { fontSize: '26px', fontWeight: '500', color: theme.colors.textPrimary, marginBottom: '6px' },
  welcomeMsg: { color: theme.colors.textSecondary, fontSize: theme.fonts.size.lg, marginBottom: '36px' },
  sectionTitle: {
    color: theme.colors.textSecondary,
    fontSize: theme.fonts.size.xs,
    fontWeight: '600',
    letterSpacing: '0.12em',
    textTransform: 'uppercase',
    marginBottom: '16px',
  },
  info: { color: theme.colors.textSecondary, fontSize: theme.fonts.size.base },
  errorMsg: { color: '#e74c3c', fontSize: theme.fonts.size.base },
  grid: { display: 'flex', flexWrap: 'wrap', gap: '12px' },
  card: {
    backgroundColor: theme.colors.bgCard,
    border: `1px solid ${theme.colors.border}`,
    borderRadius: '8px',
    padding: '24px 28px',
    cursor: 'pointer',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '10px',
    minWidth: '140px',
    transition: 'background 0.15s, border-color 0.15s',
  },
  cardName: {
    fontSize: theme.fonts.size.base,
    fontWeight: '600',
    color: theme.colors.textPrimary,
    letterSpacing: '0.05em',
  },
};

export default Home;